import { request } from "umi";
import { paramsSerializer } from "../../api";
import { LS_TOKEN_NAME } from "@/constants";
import { registerWsHandler, WsEventType, onSocketReconnected } from "../../websocket";

const urlPrefix = "/api/jobs";
const urlTenantPrefix = "/api/tenants/jobs"

/**
 * Get Dispatch list
 *
 * GET api/jobs
 */
export async function getDispatchListByPage(params: API.PageParams & Partial<API.Dispatch>, sort?: any, filter?: any) {
    return request<API.ResultObject<API.PaginatedResult<API.Dispatch>>>(`${urlPrefix}/index`, {
        method: "GET",
        params: {
            ...params,
            perPage: params.pageSize,
            page: params.current,
            sort,
            filter,
        },
        withToken: true,
        paramsSerializer,
    }).then((res) => {
        // A failure envelope ({success:false, message}) or a malformed/non-JSON
        // body resolves with no `data`; throw the server's message so ProTable's
        // onRequestError toasts it instead of a raw TypeError.
        if (!res?.success || !res.data) {
            throw new Error((res as any)?.message ?? "Failed to load dispatch list");
        }
        return {
            data: res.data.rows ?? [],
            success: res.success,
            total: res.data.pagination?.total_records ?? 0,
            pagination: res.data.pagination,
        };
    });
}


/**
 * Search Dispatch list
 *
 * GET api/jobs/search_dispatches
 */
export async function searchDispatch(params: API.DispatchSearchParams) {
    return request<API.ResultArray<API.Dispatch>>(`${urlPrefix}/search_dispatches`, {
        method: "GET",
        params: {
            ...params
        },
        withToken: true,
        paramsSerializer,
    }).then((res) => (res));
}

/**
 * Search Dispatch list (lite version for dashboard)
 * Loads only essential relations: status, appointment, user, customer, items, tags
 *
 * GET api/jobs/search_dispatches_lite
 */
export async function searchDispatchLite(params: API.DispatchSearchParams) {
    return request<API.ResultArray<API.Dispatch>>(`${urlPrefix}/search_dispatches_lite`, {
        method: "GET",
        params: {
            ...params
        },
        withToken: true,
        paramsSerializer,
    }).then((res) => (res));
}

/**
 * Fetch a single dispatch with full relations for the detail panel.
 *
 * GET api/jobs/get_dispatch?id={id}
 */
export async function getDispatchById(id: number, params?: any) {
    return request<API.ResultObject<API.Dispatch>>(`${urlPrefix}/get_dispatch`, {
        method: "GET",
        params: {
            id,
            with: params?.with ?? "dispatchContracts,dispatchCoverages,dispatchHightlights,dispatchPayments,dispatchNccs,dispatchItemSymptoms,dispatchCustomerProperties,surveyFormTriggerExecutions,salesStatus,customer,customer.customerPhones,dispatchCustomers.customer.customerPhones,addresses,addresses.customerPropertyGeolocation",
        },
        withToken: true,
        paramsSerializer,
    }).then((res) => res);
}

/**
 * Fetch a single dispatch for the detail panel, including follow_up_summary.
 * Use this instead of getDispatchById wherever the detail panel is shown —
 * the extra follow-up COUNT queries are intentionally isolated to this endpoint.
 *
 * GET api/jobs/get_dispatch_detail?id={id}
 */
export async function getDispatchForDetail(id: number, params?: any) {
    return request<API.ResultObject<API.Dispatch>>(`${urlPrefix}/get_dispatch_detail`, {
        method: "GET",
        params: {
            id,
            with: params?.with ?? "dispatchContracts,dispatchCoverages,dispatchHightlights,dispatchPayments,dispatchNccs,dispatchItemSymptoms,dispatchCustomerProperties,surveyFormTriggerExecutions,salesStatus,customer,customer.customerPhones,dispatchCustomers.customer.customerPhones,addresses,addresses.customerPropertyGeolocation",
        },
        withToken: true,
        paramsSerializer,
    }).then((res) => res);
}

export interface UnverifiedAddressItem {
    dispatch_id: number;
    external_id?: string | number | null;
    customer_id?: number | null;
    customer_name?: string | null;
    appointment_date?: string | null;
    /** Full property incl. customer_property_geolocation, ready for PinAddressModal */
    property?: API.CustomerProperty | null;
    created_at?: string;
}

/**
 * [GET] /api/jobs/tenants/unverified_addresses
 * Active jobs whose property pin is only the geocoder's nearest guess
 * (geolocation accuracy=approximate) — feeds the topbar badge. Self-clearing:
 * pinning the location (accuracy=manual) drops the job from this list.
 */
export async function getUnverifiedAddresses() {
    return request<API.ResultObject<{ items: UnverifiedAddressItem[]; count: number }>>(
        `${urlPrefix}/tenants/unverified_addresses`,
        {
            method: "GET",
            withToken: true,
        },
    ).then((res) => res.data ?? { items: [], count: 0 });
}

// ─── Unverified-addresses event bus ───────────────────────────────────────────
// Signal-only: the backend pushes this when a pin's accuracy, a job's property
// link, a job's open/closed state or an appointment date changes the list
// (TopbarBadgeService), and the badge re-fetches over REST. Not coalesced here:
// the badge spreads and coalesces its re-fetch itself (useJitteredReload).
const UNVERIFIED_ADDRESSES_UPDATED_EVENT = 'unverifiedAddressesUpdated';

export function emitUnverifiedAddressesUpdated() {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(UNVERIFIED_ADDRESSES_UPDATED_EVENT));
}

/** Subscribe to unverified-addresses pushes; returns an unsubscribe function. */
export function onUnverifiedAddressesUpdated(handler: () => void) {
    if (typeof window === 'undefined') return () => {};
    window.addEventListener(UNVERIFIED_ADDRESSES_UPDATED_EVENT, handler);
    return () => window.removeEventListener(UNVERIFIED_ADDRESSES_UPDATED_EVENT, handler);
}

registerWsHandler(WsEventType.UNVERIFIED_ADDRESSES_UPDATED, emitUnverifiedAddressesUpdated);

// A dropped push during a disconnect never arrives on its own — resync once
// the socket comes back.
onSocketReconnected(emitUnverifiedAddressesUpdated);

export interface TechnicianDescriptionPreview {
    job_id: number;
    mode: "full" | "summary";
    text: string;
    summary: {
        company: string | null;
        org_code: string | null;
        plan: string | null;
        effective_date: string | null;
        refrigerant: { label: string | null; class: "limited" | "full" | "unknown" };
        units: number;
        items: Array<{ id: number | null; description: string; status: string | null; symptoms: string[]; attributes: Record<string, string> }>;
        job_details: string[];
        office_notes: Array<{ id?: number; text: string; by?: string | null; at?: string | null; application?: string | null }>;
        no_trade_fee: boolean;
        payment_type: string | null;
        extraction_source: "rule" | "ai" | "none";
        tags: string[];
    } | null;
}

/**
 * What the technician app shows for this job (details-panel "Tech view").
 *
 * GET api/jobs/get_technician_description?id={id}
 */
export async function getTechnicianDescription(id: number) {
    return request<API.ResultObject<TechnicianDescriptionPreview>>(`${urlPrefix}/get_technician_description`, {
        method: "GET",
        params: { id },
        withToken: true,
    }).then((res) => res);
}

/**
 * Fetch the public tracking URL for a dispatch.
 * Returns null tracking_url when no GPS record / token exists yet (driver not assigned).
 *
 * GET api/jobs/get_dispatch_tracking_link?id={id}
 */
export async function getDispatchTrackingLink(id: number): Promise<{ tracking_url: string | null }> {
    return request<any>(`${urlPrefix}/get_dispatch_tracking_link`, {
        method: "GET",
        params: { id },
        withToken: true,
    }).then((res) => res.data ?? res);
}

export interface DispatchUpdatesResponse {
    updated: API.Dispatch[];
    removed_ids: number[];
    server_timestamp: string;
}

/** Paginated response shape from GET api/jobs/search_dispatches_queue */
export interface DispatchQueueResponse {
    data: API.Dispatch[];
    has_more: boolean;
    next_after_id: number | null;
}

export interface DispatchQueueParams {
    jobStatus?: string;
    without?: string;
    after_id?: number;
    limit?: number;
}

export interface DispatchScheduledParams {
    jobStatus?: string;
    startDate?: string;
    endDate?: string;
    without?: string;
}

/**
 * Fetch one page of queue dispatches (no active technician assignment).
 * Used by fetchAllQueuePages to accumulate the full queue via keyset pagination.
 *
 * GET api/jobs/search_dispatches_queue
 */
export async function searchDispatchQueue(params: DispatchQueueParams) {
    return request<DispatchQueueResponse>(`${urlPrefix}/search_dispatches_queue`, {
        method: "GET",
        params,
        withToken: true,
        paramsSerializer,
    }).then((res) => res);
}

/**
 * Fetch scheduled dispatches (technician-assigned) scoped to a date range.
 * Also includes unscheduled assigned jobs (no appointment date).
 *
 * GET api/jobs/search_dispatches_scheduled
 */
export async function searchDispatchScheduled(params: DispatchScheduledParams) {
    return request<API.ResultArray<API.Dispatch>>(`${urlPrefix}/search_dispatches_scheduled`, {
        method: "GET",
        params,
        withToken: true,
        paramsSerializer,
    }).then((res) => res);
}

/**
 * Get dispatches updated since a given timestamp (for real-time polling)
 *
 * GET api/jobs/dispatch_updates
 */
export async function getDispatchUpdates(params: { since: string; jobStatus?: string }) {
    return request<DispatchUpdatesResponse>(`${urlPrefix}/dispatch_updates`, {
        method: "GET",
        params,
        withToken: true,
        paramsSerializer,
    }).then((res) => (res));
}

/**
 * Assign Dispatch
 *
 * POST api/jobs/assign
 */
export async function assignDispatch(data: API.DispatchAssignPayload, options?: { [key: string]: any }) {
    return request<Record<string, any>>(`${urlPrefix}/assign`, {
        method: "POST",
        data: data,
        withToken: true,
        ...(options || {}),
    }).then((res) => (res));
}

/**
 * Accept or Decline Dispatch
 *
 * POST api/jobs/accept_decline
 */
export async function acceptOrDeclineDispatch(data: API.DispatchAcceptOrDeclinePayload, options?: { [key: string]: any }) {
    return request<Record<string, any>>(`${urlPrefix}/accept_decline`, {
        method: "POST",
        data: data,
        withToken: true,
        ...(options || {}),
    }).then((res) => (res));
}

/**
 * Archive a job (permission dispatch.archive). Same write as the
 * stale-dispatch cron: flow_step=archived + job_status=150 (On Hold), so the
 * job leaves the EDB and the technician app (the Job List keeps it, tagged Archived).
 *
 * POST api/jobs/tenants/archiveDispatch
 */
export async function archiveDispatch(jobId: number | string, options?: { [key: string]: any }) {
    return request<Record<string, any>>(`${urlPrefix}/tenants/archiveDispatch`, {
        method: "POST",
        data: { job_id: jobId },
        withToken: true,
        ...(options || {}),
    }).then((res) => (res));
}

/**
 * Bring an archived job back (permission dispatch.archive). It returns to the
 * appointment flow as In Progress (technician assigned) or Accepted (queue).
 *
 * POST api/jobs/tenants/unarchiveDispatch
 */
export async function unarchiveDispatch(jobId: number | string, options?: { [key: string]: any }) {
    return request<Record<string, any>>(`${urlPrefix}/tenants/unarchiveDispatch`, {
        method: "POST",
        data: { job_id: jobId },
        withToken: true,
        ...(options || {}),
    }).then((res) => (res));
}

/**
 * Update Dispatch Status
 *
 * POST api/jobs/update_job_status
 */
export async function updateJobStatus(data: FormData, options?: { [key: string]: any }) {
    // Add current FCM token to exclude it from notifications
    const currentFcmToken = localStorage.getItem('fcm_token');
    if (currentFcmToken) {
        data.append('current_fcm_token', currentFcmToken);
    }

    return request<Record<string, any>>(`${urlPrefix}/update_job_status`, {
        method: "POST",
        data: data,
        withToken: true,
        ...(options || {}),
    }).then((res) => (res));
}

/**
 * Update Dispatch Status
 *
 * POST api/jobs/update_job_meta
 */
export async function updateJobMeta(data: FormData, options?: { [key: string]: any }) {
    return request<Record<string, any>>(`${urlPrefix}/tenants/update_job_meta`, {
        method: "POST",
        data: data,
        withToken: true,
        ...(options || {}),
    }).then((res) => (res));
}

/**
 * Update Dispatch
 *
 * POST api/jobs/update
 */
export async function updateJob(data: API.DispatchUpdatePayload, options?: { [key: string]: any }) {
    return request<Record<string, any>>(`${urlPrefix}/update`, {
        method: "POST",
        data: data,
        withToken: true,
        ...(options || {}),
    }).then((res) => (res));
}

/**
 * Update Dispatch Sequence
 *
 * POST api/jobs/update_sequence
 */
export async function updateJobSequence(data: API.DispatchSequenceUpdatePayload, options?: { [key: string]: any }) {
    return request<Record<string, any>>(`${urlPrefix}/update_sequence`, {
        method: "POST",
        data: data,
        withToken: true,
        ...(options || {}),
    }).then((res) => (res));
}

/**
 * Get Dispatch Comments
 *
 * POST api/jobs/get_employee_comments
 */
export async function getJobComments(params: API.DispatchCommentPayload) {
    return request<Record<string, any>>(`${urlTenantPrefix}/get_employee_comments`, {
        method: "GET",
        params: {
            ...params
        },
        withToken: true,
        paramsSerializer,
    }).then((res) => (res));
}

export async function createDispatchJob(data: any) {
    return request('/api/jobs/tenants/create_job', {
        method: 'POST',
        data,
    });
}

/**
 * Get customer jobs by customer_id
 * GET /api/jobs/get_customer_jobs
 */
export async function getCustomerJobs(params: API.PageParams & { customer_id: number }, sort?: any, filter?: any) {
    return request<API.ResultObject<API.PaginatedResult<API.Dispatch>>>(`${urlPrefix}/get_customer_jobs`, {
        method: 'GET',
        params: {
            ...params,
            perPage: params.pageSize ?? 10,
            page: params.current ?? 1,
            sort,
            filter,
        },
        withToken: true,
        paramsSerializer,
    }).then((res) => ({
        data: res.data?.rows ?? [],
        success: res.success ?? false,
        total: res.data?.pagination?.total_records ?? 0,
        pagination: res.data?.pagination,
    }));
}

/**
 * Get time slots for a specific date
 * GET /api/jobs/tenants/getTimeSlots
 */
export async function getTimeSlotsForDate(params: { appointment_date: string; user_id?: string | number }) {
    return request<API.ResultObject<API.TimeSlot[]>>(`${urlPrefix}/tenants/getTimeSlots`, {
        method: 'GET',
        params,
        withToken: true,
        paramsSerializer,
    });
}

export interface RecoverableAppointment {
    id: number;
    appointment_date: string;
    time_from?: string | null;
    time_to?: string | null;
    /** Technician the slot would be restored for. */
    user_id?: number | null;
    technician_name?: string | null;
    deleted_at?: string | null;
    deleted_by?: string | null;
    /** Why it was removed (DispatchAppointment::REMOVAL_REASONS code) and its label. */
    removal_reason?: string | null;
    removal_reason_label?: string | null;
    removal_note?: string | null;
}

export interface RecoverableAppointmentResult {
    /** null when the job has nothing to restore (already scheduled, or no recent future-dated removal). */
    recoverable: RecoverableAppointment | null;
    /** Jobs on the technician's route that overlap the original slot. Empty → original time is free. */
    conflicts: { dispatch_id: number; customer_name?: string | null; time_from?: string | null; time_to?: string | null }[];
    /** Next free slot of the same duration on that date; only set when `conflicts` is non-empty. */
    suggested_slot: { time_from: string; time_to: string } | null;
}

/**
 * Recently removed appointment that could be put back on a job (offered when
 * a technician is re-added), with conflict check against `user_id`'s route.
 * GET /api/tenant/dispatch-appointments/recoverable
 */
export async function getRecoverableAppointment(params: { job_id: number | string; user_id?: number | string }) {
    return request<API.ResultObject<RecoverableAppointmentResult>>(`/api/tenant/dispatch-appointments/recoverable`, {
        method: 'GET',
        params,
        withToken: true,
        paramsSerializer,
    });
}

/**
 * Job context attached to every schedule-conflict row (overlap job and
 * orphan alike) so the dispatcher can tell the jobs apart — and open the
 * right one — from the topbar bell without visiting each job first.
 */
export interface ScheduleConflictJobContext {
    customer_id: number | null;
    /** Job-site address, formatted ("123 Main St, City, ST 12345"). */
    address: string | null;
    phone: string | null;
    dispatch_type: string | null;
    trade: string | null;
    /** Current EDB status label (e.g. "Customer Appointment Set"). */
    status_label: string | null;
}

export interface ScheduleConflictJob extends ScheduleConflictJobContext {
    job_id: number;
    customer_name: string;
    time_from: string;
    time_to: string;
}

export interface ScheduleConflictGroup {
    user_id: number;
    technician_name: string | null;
    appointment_date: string;
    jobs: ScheduleConflictJob[];
}

/**
 * An active appointment whose user_id is no longer an assigned technician on
 * the job (half-completed reassignment). It occupies no schedule time, but
 * the stale row needs a dispatcher to resolve: re-point it at the current
 * technician (pick a slot) or release it when the job is unassigned.
 */
export interface OrphanedAppointment extends ScheduleConflictJobContext {
    appointment_id: number;
    job_id: number;
    customer_name: string;
    appointment_date: string;
    time_from: string | null;
    time_to: string | null;
    phantom_user_id: number;
    phantom_technician_name: string | null;
    current_user_id: number | null;
    current_technician_name: string | null;
}

export type OrphanResolveAction = 'release' | 'assign_current' | 'readd_primary' | 'readd_secondary';

/**
 * Resolve an orphaned appointment row (row-id based, duplicate-safe).
 * POST /api/jobs/tenants/resolveOrphanAppointment
 */
export async function resolveOrphanAppointment(data: {
    appointment_id: number;
    action: OrphanResolveAction;
    time_from?: string;
    time_to?: string;
    /** Required for action 'release' (a real removal). */
    removal_reason?: string;
    removal_note?: string;
}) {
    return request<API.ResultObject<any>>(`${urlPrefix}/tenants/resolveOrphanAppointment`, {
        method: 'POST',
        data,
        withToken: true,
    });
}

/**
 * List overlapping appointment time slots grouped by technician & date,
 * plus orphaned appointments needing cleanup.
 * GET /api/jobs/tenants/getScheduleConflicts
 */
export async function getScheduleConflicts() {
    return request<API.ResultObject<ScheduleConflictGroup[]> & { orphans?: OrphanedAppointment[] }>(`${urlPrefix}/tenants/getScheduleConflicts`, {
        method: 'GET',
        withToken: true,
        paramsSerializer,
    });
}

// ─── Schedule-conflicts event bus ─────────────────────────────────────────────
// Signal-only, unlike the call-queue/reschedule-request pushes: computing the
// actual conflict list means two multi-join queries across the tenant's whole
// future-dated schedule (JobsApi::getScheduleConflicts), too expensive to run
// on the backend's hot appointment-write path just to attach a payload. The
// push here just means "something that can affect conflicts changed" — the
// listener re-fetches over REST, same as ConflictBell's popover-open refresh.
const SCHEDULE_CONFLICTS_UPDATED_EVENT = 'scheduleConflictsUpdated';

export function emitScheduleConflictsUpdated() {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(SCHEDULE_CONFLICTS_UPDATED_EVENT));
}

/** Subscribe to conflict-changed pushes; returns an unsubscribe function. */
export function onScheduleConflictsUpdated(handler: () => void) {
    if (typeof window === 'undefined') return () => {};
    window.addEventListener(SCHEDULE_CONFLICTS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(SCHEDULE_CONFLICTS_UPDATED_EVENT, handler);
}

// Several schedule writes in one request (DispatchStatus, DispatchUser, DispatchAppointment
// can each publish this) otherwise trigger one conflicts re-fetch per write — coalesce a
// burst into a single re-fetch since this push is signal-only anyway.
registerWsHandler(WsEventType.SCHEDULE_CONFLICTS_UPDATED, emitScheduleConflictsUpdated, { coalesceMs: 300 });

// A dropped push during a disconnect never arrives on its own — resync the
// instant the socket comes back instead of relying on the next real change.
onSocketReconnected(emitScheduleConflictsUpdated);

/**
 * Update assigned technicians for a job
 *
 * POST /api/jobs/updateDispatchTech
 */
export async function updateDispatchTech(data: {
    job_id: string;
    assigned_techs: string;
    replace_user_id?: number;
    /** Required when unassigning a job that has a scheduled appointment (see confirmAppointmentRemoval). */
    removal_reason?: string;
    removal_note?: string;
}) {
    return request<API.ResultObject<any>>(`${urlPrefix}/tenants/updateDispatchTech`, {
        method: "POST",
        data,
        withToken: true,
        paramsSerializer,
    });
}

/**
 * Assign dispatch to a technician as unscheduled (no appointment)
 * Preserves the tech assignment, soft-deletes appointment, sets status to Accepted (260)
 * Optional replace_user_id: if set, only that tech slot is swapped (multi-tech drag-move).
 *
 * POST /api/jobs/tenants/assignDispatchUnscheduled
 */
export async function assignDispatchUnscheduled(data: {
    job_id: string;
    assigned_techs: string;
    replace_user_id?: number;
    /** Required when the job has a scheduled appointment (it is soft-deleted). */
    removal_reason?: string;
    removal_note?: string;
}) {
    return request<API.ResultObject<any>>(`${urlPrefix}/tenants/assignDispatchUnscheduled`, {
        method: "POST",
        data,
        withToken: true,
        paramsSerializer,
    });
}

/**
 * Add a technician to a multi-tech job without disturbing existing assignments.
 *
 * POST /api/jobs/tenants/addDispatchTechnician
 */
export async function addDispatchTechnician(data: {
    job_id: number;
    user_id: number;
    /** Add the technician directly as primary (demotes the current primary). Default: primary only when the job had no technician. */
    is_primary?: boolean;
}) {
    return request<API.ResultObject<any>>(`${urlPrefix}/tenants/addDispatchTechnician`, {
        method: "POST",
        data,
        withToken: true,
        paramsSerializer,
    });
}

/**
 * Remove a single technician from a multi-tech job.
 * If the removed tech was primary, the next active tech is promoted automatically.
 *
 * POST /api/jobs/tenants/removeDispatchTechnician
 */
export async function removeDispatchTechnician(data: {
    job_id: number;
    user_id: number;
    /** Required when removing the LAST technician of a job with a scheduled appointment. */
    removal_reason?: string;
    removal_note?: string;
}) {
    return request<API.ResultObject<any>>(`${urlPrefix}/tenants/removeDispatchTechnician`, {
        method: "POST",
        data,
        withToken: true,
        paramsSerializer,
    });
}

/**
 * Make one of a job's assigned technicians the primary technician.
 * The primary owns the shared appointment slot (its schedule is what the
 * overlap guard checks), gets the technician reminder SMS, is the "technician"
 * in customer SMS / tracking and is who reporting attributes the job to.
 * 409 APPT_OVERLAP when the new primary's day conflicts; 423 when a route is locked.
 *
 * POST /api/jobs/tenants/setPrimaryTechnician
 */
export async function setPrimaryTechnician(data: { job_id: number; user_id: number }) {
    return request<API.ResultObject<any> & { primary_user_id?: number; previous_primary_user_id?: number | null }>(
        `${urlPrefix}/tenants/setPrimaryTechnician`,
        {
            method: "POST",
            data,
            withToken: true,
            paramsSerializer,
        },
    );
}

/**
 * Update dispatch appointment
 *
 * POST /api/jobs/tenants/updateDispatchAppt
 */
export async function updateDispatchAppt(data: {
    job_id: string;
    user_id?: string;
    appointment_date?: string;
    appointment_time?: string;
    time_from?: string;
    time_to?: string;
    stop_sequence?: number
}) {
    return request<API.ResultObject<any>>(`${urlPrefix}/tenants/updateDispatchAppt`, {
        method: "POST",
        data,
        withToken: true,
    });
}

/**
 * Update dispatch priority
 *
 * POST /api/jobs/tenants/updateDispatchPriority
 */
export async function updateDispatchPriority(data: {
    job_id: string | number;
    priority?: string;
    priority_term_id?: number;
}) {
    return request<API.ResultObject<any>>(`${urlPrefix}/tenants/updateDispatchPriority`, {
        method: "POST",
        data,
        withToken: true,
    });
}

/**
 * Update dispatch property (which of the customer's properties this dispatch is mapped to)
 *
 * POST /api/jobs/tenants/updateDispatchProperty
 */
export async function updateDispatchProperty(data: {
    job_id: string | number;
    property_id: number;
}) {
    return request<API.ResultObject<any>>(`${urlPrefix}/tenants/updateDispatchProperty`, {
        method: "POST",
        data,
        withToken: true,
    });
}

/**
 * Create a follow-up dispatch
 *
 * POST /api/dispatch/tenants/create_follow_up
 *
 * Payload shape (new): { main_dispatch_id, copy_flags, fields }.
 * The backend remains backward compatible with the legacy flat payload, but new
 * callers should always send copy_flags + fields so per-section copy is honored.
 */
export interface CreateFollowUpDispatchPayload {
    main_dispatch_id: number;
    copy_flags?: {
        priority?: boolean;
        technician?: boolean;
        appointment?: boolean;
        service_types?: boolean;
        tags?: boolean;
        comments?: boolean;
    };
    fields?: {
        priority_term_id?: number | null;
        user_id?: number | null;          // legacy: single tech (back-compat)
        user_ids?: number[] | null;       // multi-tech: first id becomes primary
        primary_user_id?: number | null;  // explicit primary among user_ids (moved to the front server-side)
        appointment_date?: string | null; // 'YYYY-MM-DD' or full datetime
        time_from?: string | null;        // 'HH:mm:ss'
        time_to?: string | null;          // 'HH:mm:ss'
        service_type_term_ids?: number[];
        tag_term_ids?: number[];
    };
}

export async function createFollowUpDispatch(data: CreateFollowUpDispatchPayload) {
    return request<API.ResultObject<API.Dispatch>>('/api/dispatch/tenants/create_follow_up', {
        method: "POST",
        data,
        withToken: true,
    });
}

/**
 * Get follow-up dispatches for a main dispatch
 *
 * GET /api/dispatch/tenants/follow_ups
 */
export async function getFollowUpDispatches(params: { main_dispatch_id: number }) {
    return request<API.ResultObject<{
        main_dispatch_id: number;
        main_dispatch: API.Dispatch;
        follow_up_dispatches: API.Dispatch[];
        total_follow_ups: number;
    }>>('/api/dispatch/tenants/follow_ups', {
        method: "GET",
        params,
        withToken: true,
        paramsSerializer,
    });
}

/**
 * Update visit type of a dispatch
 *
 * PUT /api/dispatch/tenants/update_visit_type
 */
export async function updateVisitType(data: {
    dispatch_id: number;
    visit_type: 'main' | 'follow_up';
    main_dispatch_id?: number;
}) {
    return request<API.ResultObject<API.Dispatch>>('/api/dispatch/tenants/update_visit_type', {
        method: "PUT",
        data,
        withToken: true,
    });
}

/**
 * Export the filtered dispatch list as a CSV file download.
 *
 * GET api/jobs/export
 */
export async function exportDispatchListToCsv(params: Record<string, any>) {
    const token = localStorage.getItem(LS_TOKEN_NAME) || '';
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) {
            value.forEach((v) => query.append(`${key}[]`, String(v)));
        } else {
            query.set(key, String(value));
        }
    });
    const url = `${API_URL}${urlPrefix}/export?${query.toString()}`;
    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `dispatch_list_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
}

/**
 * Get address autocomplete suggestions for ft_address search.
 *
 * GET api/jobs/address_suggestions?q=...
 */
export async function getAddressSuggestions(q: string) {
    return request<API.ResultObject<{ label: string; value: string; property_id: number }[]>>(`${urlPrefix}/address_suggestions`, {
        method: "GET",
        params: { q },
        withToken: true,
    });
}

/**
 * Edit the description of a direct job (permission dispatch.description.update):
 * rewrites the Job Notes coverage note the CSR typed at creation. An empty
 * string clears it. Warranty jobs are refused by the API (409) because their
 * notes come from the provider feed.
 *
 * POST api/jobs/tenants/updateDispatchDescription
 */
export async function updateDispatchDescription(
    jobId: number | string,
    description: string,
    options?: { [key: string]: any },
) {
    return request<API.ResultObject<{ description: string; changed: boolean; change?: "created" | "updated" | "cleared" }>>(
        `${urlPrefix}/tenants/updateDispatchDescription`,
        {
            method: "POST",
            data: { job_id: jobId, description },
            withToken: true,
            ...(options || {}),
        },
    ).then((res) => res);
}
